import { Spinner } from "@postdaily/ui/components/spinner";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import {
  safeReferralDestination,
  storeCreatorReferral,
} from "@/lib/creator-referral";
import { createNoIndexHead } from "@/lib/seo";
import { useTRPC } from "@/utils/trpc";

/**
 * A creator's short link: `postdaily.app/r/CREATOR20`.
 *
 * Read aloud in a video, so it has to survive being typed by hand — which is
 * why it carries the discount code itself rather than an opaque id.
 *
 * This renders rather than redirecting in `beforeLoad` for two reasons: the
 * remembered code has to be written to the visitor's browser, and the visit
 * has to be counted from the visitor's own connection. A server-side redirect
 * completes without any client code running, and a loader would count the
 * click against the server's address. The screen below is what one network
 * round trip looks like; it is never a destination.
 */
export const Route = createFileRoute("/(public)/r/$code")({
  head: () => createNoIndexHead("Redirecting"),
  component: CreatorReferralLanding,
});

function CreatorReferralLanding() {
  const { code } = Route.useParams();
  const trpc = useTRPC();
  const navigate = useNavigate();

  const normalized = code.trim().toUpperCase();
  // A malformed code fails input validation rather than returning null, and
  // retrying will not make it valid — every outcome bounces to pricing.
  const { mutate } = useMutation(
    trpc.billing.followCreatorLink.mutationOptions({ retry: false }),
  );
  // Fired exactly once. A ref rather than state, because StrictMode mounts the
  // effect twice in development and a counted click must not double.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) {
      return;
    }

    fired.current = true;

    mutate(
      { code: normalized },
      {
        onSettled: (match) => {
          // Only a live code is remembered. Storing an unknown one would leave
          // a value that can never apply sitting in the browser for two
          // months, quietly shadowing a real code the visitor picks up later.
          //
          // Whether it reaches the device at all is decided inside
          // `storeCreatorReferral`, which writes nothing until the visitor has
          // allowed referral tracking. Before that the code is held in memory,
          // so someone who accepts on the banner a moment later — on the
          // pricing page this navigates to — is still attributed. That works
          // because a client-side navigation keeps the same JS context.
          if (match) {
            storeCreatorReferral(match.code);
          }

          void navigate({
            href: safeReferralDestination(match?.landingPath),
            replace: true,
          });
        },
      },
    );
  }, [mutate, navigate, normalized]);

  return (
    <main className="flex min-h-[60vh] items-center justify-center">
      <div className="flex items-center gap-3 text-muted-foreground text-sm">
        <Spinner />
        <span>Taking you to PostDaily...</span>
      </div>
    </main>
  );
}
